import {
  Badge,
  Box,
  Group,
  Image,
  ImageProps,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import React from 'react';
import { Link } from 'react-router-dom';

import { OrderType } from '@portals/api/partners';
import { useGenericContext } from '@portals/framework';
import { useAppConfig } from '@portals/framework/context';
import { ReactComponent as Gallery } from '@portals/icons/bold/gallery.svg';
import { ReactComponent as InfoCircle } from '@portals/icons/linear/info-circle.svg';
import { ProductPricingModel, TenantType } from '@portals/types';
import { formatCurrency } from '@portals/utils';

interface OrderProductSummaryRowProps {
  orderItem: OrderType['items'][number];
  index: number;
  withSku: boolean;
}

export function OrderProductSummaryRow({
  orderItem,
  index,
  withSku,
}: OrderProductSummaryRowProps) {
  const { tenantType } = useAppConfig();
  const theme = useMantineTheme();
  const order = useGenericContext<OrderType>();

  const getInterval = (
    paymentInterval: OrderType['items'][number]['payment_interval']
  ) => {
    if (paymentInterval === 'monthly') return ' / month';
    else if (paymentInterval === 'yearly') return ' / year';

    return '';
  };

  return (
    <Box
      py="md"
      w="100%"
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: withSku
          ? '3fr 1fr 1.5fr 1fr 1.5fr'
          : '3fr repeat(2, 1fr) 1.5fr',
        gap: theme.spacing.xl,
        borderTop: index === 0 ? 'none' : `1px solid ${theme.colors.gray[2]}`,
      })}
    >
      <Group noWrap spacing="xl" p={0}>
        <Image
          withPlaceholder
          src={orderItem.product.image_url}
          styles={imageStyles}
          radius="md"
          placeholder={
            <Gallery
              width="50%"
              height="50%"
              color={theme.colors.blue_gray[2]}
            />
          }
        />

        <Stack py="xs" h="100%" spacing="xs" justify="space-between">
          {orderItem.store_listing.published &&
          tenantType === TenantType.Organization ? (
            <Link to={`/store/${orderItem.store_listing.id}`} target="_blank">
              <Text size="sm" weight={500} inherit>
                {orderItem.product.name}
              </Text>
            </Link>
          ) : (
            <Text size="sm" weight={500} color="blue_gray.9">
              {orderItem.product.name}
            </Text>
          )}

          <Group>
            <Badge
              size="md"
              color="gray.1"
              radius="lg"
              variant="filled"
              c="gray.6"
              fw={300}
            >
              {orderItem.quantity}x
            </Badge>
          </Group>
        </Stack>
      </Group>

      <Stack py="xs">
        <Text size="sm" weight={500} color="blue_gray.9">
          Type
        </Text>

        <Text size="sm" color="gray.5" transform="capitalize">
          {orderItem.product.product_type}
        </Text>
      </Stack>

      <Stack py="xs">
        <Text size="sm" weight={500} color="blue_gray.9">
          Price
        </Text>

        {orderItem.product.pricing_model === ProductPricingModel.UsageBased ? (
          <Text size="sm" color="gray.5">
            {formatCurrency(
              orderItem.total_usage_based_price_in_scu,
              order.currency
            )}{' '}
            / {orderItem.product.usage_based_unit_name}
          </Text>
        ) : (
          <Text size="sm" color="gray.5">
            {formatCurrency(orderItem.total_price_in_scu, order.currency)}
            {getInterval(orderItem.payment_interval)}
          </Text>
        )}
      </Stack>

      {withSku ? (
        <Stack py="xs">
          <Text size="sm" weight={500} color="blue_gray.9">
            SKU
          </Text>

          <Text
            size="sm"
            transform="capitalize"
            color="gray.5"
            data-testid="sku-value"
          >
            {orderItem.product.sku || 'N/A'}
          </Text>
        </Stack>
      ) : null}

      <Stack py="xs">
        <Group align="center" spacing="xs">
          <Text size="sm" weight={500} color="blue_gray.9">
            Charged Amount
          </Text>

          <Tooltip
            withinPortal
            width={400}
            multiline
            label={`"Charged Amount" refers to the actual cost accrued for using this product during the specified time period. This cost is calculated based on the applicable pricing model, which can involve proportional calculations corresponding to the usage duration or direct correlation to the extent of actual usage.`}
          >
            <InfoCircle width={16} height={16} color={theme.colors.gray[5]} />
          </Tooltip>
        </Group>

        <Text size="sm" transform="capitalize" color="gray.5">
          {formatCurrency(
            orderItem.total_charged_amount_in_scu,
            order.currency
          )}
        </Text>
      </Stack>
    </Box>
  );
}

const imageStyles: ImageProps['styles'] = {
  root: {
    width: '64px !important',
    height: '64px !important',
  },
  figure: { height: 64, width: 64 },
  imageWrapper: { height: 64, width: 64 },
};
